/**
 * GapAssignee MDXEditor plugin — inline mention chip for a user.
 *
 * MARKDOWN SYNTAX (text-level directive):
 *   :GapAssignee{userId="abc" name="Jane Porter" color="pink"}
 *
 * `name` is snapshotted at insert time so chips keep rendering when a user is later
 * deactivated. `color` is a sticky avatar color derived from `userId` at insert time
 * (palette key from avatarColor.ts).
 *
 * Clicking the chip re-opens the @ mention popover so the user can swap or remove it.
 */

import { DirectiveDescriptor, DirectiveEditorProps, useMdastNodeUpdater, useCellValue, readOnly$ } from '@mdxeditor/editor'
import Box from '@mui/material/Box'
import { observer } from 'mobx-react-lite'
import { themeConfig } from '@gap/shared/theme'
import { useRef } from 'react'
import { useMarkdownStore } from '../../MarkdownStore'
import Avatar from '../../popover/Avatar'
import MentionPopover from '../../popover/MentionPopover'
import DateSubmenuPopover from '../../popover/DateSubmenuPopover'
import GapAssigneeStore from './GapAssigneeStore'
import useStore from 'reusable/mobx-store-utils'

const DIRECTIVE_NAME = 'GapAssignee'

const GapAssigneeComponent = observer(function GapAssigneeComponent({ mdastNode }: DirectiveEditorProps) {
   const isReadOnly = useCellValue(readOnly$)
   const updateMdastNode = useMdastNodeUpdater()
   const markdownStore = useMarkdownStore()
   const chipRef = useRef<HTMLSpanElement>(null)

   const gapAssigneeStore = useStore(sp => new GapAssigneeStore(sp), {
      markdownStore,
      updateMdastNode,
      attributes: mdastNode.attributes as Record<string, string> | undefined,
      chipRef
   })

   if (isReadOnly) {
      return `@${gapAssigneeStore.name} `
   }

   return (
      <>
         <Box
            component="span"
            ref={chipRef}
            onMouseDown={gapAssigneeStore.handleChipMouseDown}
            sx={{
               display: 'inline-flex',
               alignItems: 'center',
               gap: '6px',
               padding: '2px 8px 2px 4px',
               borderRadius: '9999px',
               background: gapAssigneeStore.userIsDeactivated ? themeConfig.grey[200] : themeConfig.system.info[50],
               color: gapAssigneeStore.userIsDeactivated ? themeConfig.primary[500] : themeConfig.system.info[700],
               fontSize: 14,
               fontWeight: 500,
               lineHeight: 1.2,
               cursor: 'pointer',
               verticalAlign: 'middle',
               '&:hover': { background: gapAssigneeStore.userIsDeactivated ? themeConfig.grey[300] : themeConfig.system.info[100] },
            }}
         >
            <Avatar name={gapAssigneeStore.name} colorKey={gapAssigneeStore.colorKey} size={18} deactivated={gapAssigneeStore.userIsDeactivated} />
            {gapAssigneeStore.name}
         </Box>
         {gapAssigneeStore.popover?.mode === 'mention' && (
            <MentionPopover
               anchor={gapAssigneeStore.popover.anchor}
               query=""
               team={gapAssigneeStore.team}
               clientUsers={markdownStore.sp.clientUsers || []}
               clientOrgName={markdownStore.sp.clientOrgName}
               hideDates
               onSelect={gapAssigneeStore.handleSelect}
               onClose={gapAssigneeStore.handleClose}
            />
         )}
         {gapAssigneeStore.popover?.mode === 'date' && (
            <DateSubmenuPopover
               anchor={gapAssigneeStore.popover.anchor}
               onSelect={gapAssigneeStore.handleDatePicked}
               onClose={gapAssigneeStore.handleClose}
            />
         )}
      </>
   )
})

export const GapAssigneeDirectiveDescriptor: DirectiveDescriptor = {
   name: DIRECTIVE_NAME,
   type: 'textDirective',
   testNode(node) {
      return node.name === DIRECTIVE_NAME
   },
   attributes: ['userId', 'name', 'color'],
   hasChildren: false,
   Editor: GapAssigneeComponent,
}
